// Evergreen provider catalog.
//
// Model selection is AUTO by default: the user picks a provider, and we resolve
// the cheapest chat model live from that provider's list-models API (see DISCOVERY
// below), cached for a day. No pricing is hardcoded — no provider exposes prices
// via API, so tracking them means perpetual maintenance. `fallbackModels` are only
// the offline safety net / override-dropdown seed; the live list supersedes them.
export const AUTO_MODEL = 'auto';

// Sentinel stored as the active provider when the user explicitly opts out of AI
// and wants the local data engine — even though a valid key may still be saved.
export const NO_AI = 'none';

export const AI_PROVIDERS = {
  openai: {
    id: 'openai',
    name: 'ChatGPT (OpenAI)',
    fallbackModels: [
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
      { id: 'gpt-5-mini', name: 'GPT-5 Mini' },
      { id: 'gpt-4o', name: 'GPT-4o' }
    ],
    defaultModel: 'gpt-4o-mini',
    keyPlaceholder: 'sk-...',
    keyUrl: 'https://platform.openai.com/api-keys',
    requiresKey: true
  },
  anthropic: {
    id: 'anthropic',
    name: 'Claude (Anthropic)',
    fallbackModels: [
      { id: 'claude-haiku-4-5', name: 'Claude Haiku (Fast & Cheap)' },
      { id: 'claude-sonnet-5', name: 'Claude Sonnet' },
      { id: 'claude-opus-4-8', name: 'Claude Opus (Best)' }
    ],
    defaultModel: 'claude-haiku-4-5',
    keyPlaceholder: 'sk-ant-...',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    requiresKey: true
  },
  gemini: {
    id: 'gemini',
    name: 'Gemini (Google)',
    fallbackModels: [
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
      { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' }
    ],
    defaultModel: 'gemini-2.5-flash',
    keyPlaceholder: 'AIza...',
    keyUrl: 'https://aistudio.google.com/app/apikey',
    requiresKey: true
  },
  groq: {
    id: 'groq',
    name: 'Groq (Free)',
    fallbackModels: [
      { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 8B (Fastest)' },
      { id: 'llama-3.3-70b-versatile', name: 'Llama 3.3 70B' }
    ],
    defaultModel: 'llama-3.1-8b-instant',
    keyPlaceholder: 'gsk_...',
    keyUrl: 'https://console.groq.com/keys',
    requiresKey: true
  },
  mistral: {
    id: 'mistral',
    name: 'Mistral (Mistral AI)',
    fallbackModels: [
      { id: 'mistral-small-latest', name: 'Mistral Small (Fast)' },
      { id: 'mistral-medium-latest', name: 'Mistral Medium' },
      { id: 'mistral-large-latest', name: 'Mistral Large (Best)' }
    ],
    defaultModel: 'mistral-small-latest',
    keyPlaceholder: 'API key',
    keyUrl: 'https://console.mistral.ai/api-keys',
    requiresKey: true
  },
};

// Provider display order for card grid
export const PROVIDER_ORDER = ['openai', 'anthropic', 'gemini', 'groq', 'mistral'];

// --- Evergreen model discovery ---
//
// Per provider: how to fetch the live model list, which entries are chat-capable,
// and an ordered `cheapPref` of name substrings identifying the cheap tier
// (earlier = cheaper). We never hardcode prices; "cheapest" is inferred from the
// tier name, which is how these providers name their small/fast models.
const DISCOVERY = {
  openai: {
    url: () => 'https://api.openai.com/v1/models',
    headers: (key) => ({ Authorization: `Bearer ${key}` }),
    ids: (json) => (json?.data || []).map((m) => m.id),
    isChat: (id) => /^(gpt-|o[134])/.test(id) &&
      !/(embedding|audio|realtime|image|tts|whisper|dall-e|moderation|search|transcribe|codex|instruct)/.test(id),
    cheapPref: ['nano', 'mini']
  },
  anthropic: {
    url: () => 'https://api.anthropic.com/v1/models',
    headers: (key) => ({
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    }),
    ids: (json) => (json?.data || []).map((m) => m.id),
    isChat: (id) => id.startsWith('claude-'),
    cheapPref: ['haiku', 'sonnet', 'opus']
  },
  gemini: {
    url: (key) => `https://generativelanguage.googleapis.com/v1beta/models?key=${key}`,
    headers: () => ({}),
    ids: (json) => (json?.models || [])
      .filter((m) => (m.supportedGenerationMethods || []).includes('generateContent'))
      .map((m) => (m.name || '').replace(/^models\//, '')),
    isChat: (id) => id.startsWith('gemini') && !/embedding|aqa|image|tts|live|vision-/.test(id),
    cheapPref: ['flash-lite', 'flash-8b', 'flash']
  },
  groq: {
    url: () => 'https://api.groq.com/openai/v1/models',
    headers: (key) => ({ Authorization: `Bearer ${key}` }),
    ids: (json) => (json?.data || []).map((m) => m.id),
    isChat: (id) => !/whisper|tts|guard|embed/.test(id),
    cheapPref: ['8b', 'instant', '9b']
  },
  mistral: {
    url: () => 'https://api.mistral.ai/v1/models',
    headers: (key) => ({ Authorization: `Bearer ${key}` }),
    ids: (json) => (json?.data || []).map((m) => m.id),
    isChat: (id) => !/embed|ocr|moderation/.test(id),
    cheapPref: ['ministral-3b', 'ministral-8b', 'small']
  },
};

const rankOf = (id, cheapPref) => {
  const lower = id.toLowerCase();
  for (let i = 0; i < cheapPref.length; i++) {
    if (lower.includes(cheapPref[i])) return i;
  }
  return cheapPref.length + 1; // unknown tier ranks after the known cheap tiers
};

/**
 * Fetch the provider's live model list, filtered to chat models and sorted
 * cheapest-first. Throws on network/HTTP failure.
 * @returns {Promise<string[]>} model ids, cheapest first
 */
export const discoverModels = async (providerId, apiKey, endpoint) => {
  const d = DISCOVERY[providerId];
  if (!d) return [];
  const res = await fetch(d.url(apiKey, endpoint), { headers: d.headers(apiKey) });
  if (!res.ok) throw new Error(`List models failed (${res.status})`);
  const json = await res.json();
  const ids = d.ids(json).filter((id) => id && d.isChat(id));
  ids.sort((a, b) => rankOf(a, d.cheapPref) - rankOf(b, d.cheapPref) || a.localeCompare(b));
  return ids;
};

// Cache the resolved cheapest model per provider so we don't hit the list API on every run.
const MODEL_CACHE_KEY = 'timechainstats_ai_model_cache_v1';
const MODEL_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

const readModelCache = () => {
  try {
    return JSON.parse(localStorage.getItem(MODEL_CACHE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const writeModelCache = (cache) => {
  try {
    localStorage.setItem(MODEL_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    /* ignore quota errors */
  }
};

/**
 * Resolve the model to actually call for a provider.
 * - If `stored` is a concrete override (not AUTO), use it verbatim.
 * - Otherwise return the cheapest live model (cached), falling back to defaultModel.
 */
export const resolveModel = async (providerId, apiKey, endpoint, stored = AUTO_MODEL) => {
  if (stored && stored !== AUTO_MODEL) return stored;
  const fallback = AI_PROVIDERS[providerId]?.defaultModel || null;

  const cache = readModelCache();
  const cached = cache[providerId];
  if (cached?.model && (Date.now() - cached.ts) < MODEL_CACHE_TTL_MS) return cached.model;

  try {
    const ids = await discoverModels(providerId, apiKey, endpoint);
    const chosen = ids[0] || fallback;
    if (chosen) {
      cache[providerId] = { model: chosen, ts: Date.now() };
      writeModelCache(cache);
    }
    return chosen;
  } catch (e) {
    console.warn(`[AIProviders] Model discovery failed for ${providerId}:`, e.message);
    return cached?.model || fallback;
  }
};

// --- Storage ---

const V2_STORAGE_KEY = 'timechainstats_ai_providers_v2';
const V1_STORAGE_KEY = 'timechainstats_ai_providers';
const LEGACY_STORAGE_KEY = 'timechainstats_ai_config';

/**
 * Read v2 storage: { active: string|null, keys: { [providerId]: { apiKey, model, endpoint? } } }
 */
const readStore = () => {
  try {
    const raw = localStorage.getItem(V2_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn('[AIProviders] Failed to read store:', e.message);
  }
  return { active: null, keys: {} };
};

const writeStore = (store) => {
  try {
    localStorage.setItem(V2_STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn('[AIProviders] Failed to write store:', e.message);
  }
};

/**
 * Migrate from v1 multi-provider format or legacy single-provider format to v2
 */
const migrateToV2 = () => {
  try {
    if (localStorage.getItem(V2_STORAGE_KEY)) return; // already migrated

    // Try v1 format first (timechainstats_ai_providers)
    const v1Raw = localStorage.getItem(V1_STORAGE_KEY);
    if (v1Raw) {
      const v1 = JSON.parse(v1Raw);
      const providers = v1.providers || [];
      if (providers.length > 0) {
        const keys = {};
        let active = null;
        for (const p of providers) {
          if (!p.provider || !AI_PROVIDERS[p.provider]) continue;
          const entry = { model: p.model || AI_PROVIDERS[p.provider].defaultModel };
          if (AI_PROVIDERS[p.provider].requiresKey) entry.apiKey = p.apiKey || '';
          if (AI_PROVIDERS[p.provider].requiresEndpoint) entry.endpoint = p.endpoint || 'http://localhost:11434';
          keys[p.provider] = entry;
          // First enabled provider becomes active
          if (!active && p.enabled !== false) active = p.provider;
        }
        if (!active && Object.keys(keys).length > 0) active = Object.keys(keys)[0];
        writeStore({ active, keys });
        localStorage.removeItem(V1_STORAGE_KEY);
        console.log('[AIProviders] Migrated v1 config to v2');
        return;
      }
    }

    // Try legacy single-config format (timechainstats_ai_config)
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (legacy.provider && AI_PROVIDERS[legacy.provider]) {
        const entry = { model: legacy.model || AI_PROVIDERS[legacy.provider].defaultModel };
        if (AI_PROVIDERS[legacy.provider].requiresKey) entry.apiKey = legacy.apiKey || '';
        if (AI_PROVIDERS[legacy.provider].requiresEndpoint) entry.endpoint = legacy.endpoint || 'http://localhost:11434';
        writeStore({ active: legacy.provider, keys: { [legacy.provider]: entry } });
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        console.log('[AIProviders] Migrated legacy config to v2');
      }
    }
  } catch (e) {
    console.warn('[AIProviders] Migration failed:', e.message);
  }
};

// Run migration on load
migrateToV2();

// --- New API ---

/**
 * Get saved key/model for a provider
 * @returns {{ apiKey?: string, model: string, endpoint?: string } | null}
 */
export const getProviderKey = (providerId) => {
  const store = readStore();
  return store.keys[providerId] || null;
};

/**
 * Save key/model for a provider
 */
export const setProviderKey = (providerId, apiKey, model, endpoint) => {
  const store = readStore();
  const entry = { model: model || AUTO_MODEL };
  if (AI_PROVIDERS[providerId]?.requiresKey) entry.apiKey = apiKey || '';
  if (AI_PROVIDERS[providerId]?.requiresEndpoint) entry.endpoint = endpoint || 'http://localhost:11434';
  store.keys[providerId] = entry;
  // Auto-activate if no active provider
  if (!store.active) store.active = providerId;
  writeStore(store);
};

/**
 * Remove key for a provider
 */
export const clearProviderKey = (providerId) => {
  const store = readStore();
  delete store.keys[providerId];
  if (store.active === providerId) {
    // Pick next available or null
    const remaining = Object.keys(store.keys);
    store.active = remaining.length > 0 ? remaining[0] : null;
  }
  writeStore(store);
};

/**
 * Get active provider ID
 * @returns {string|null}
 */
export const getActiveProvider = () => {
  const store = readStore();
  return store.active;
};

/**
 * Set active provider
 */
export const setActiveProvider = (providerId) => {
  const store = readStore();
  store.active = providerId;
  writeStore(store);
};

// --- Backward-compatible API (used by aiService.js and AIInsightsButton.jsx) ---

/**
 * Build a config object from provider ID + stored key data
 * Shape: { provider, apiKey, endpoint, model } — same as old format
 */
const buildConfig = (providerId) => {
  const providerDef = AI_PROVIDERS[providerId];
  if (!providerDef) return null;
  const stored = getProviderKey(providerId);
  if (!stored) return null;
  return {
    provider: providerId,
    apiKey: stored.apiKey || null,
    endpoint: stored.endpoint || null,
    model: stored.model || AUTO_MODEL
  };
};

/**
 * Get the active provider's config (backward-compatible)
 * @returns {{ provider, apiKey, endpoint, model } | null}
 */
export const getStoredConfig = () => {
  const store = readStore();
  if (!store.active || store.active === NO_AI) return null;
  const config = buildConfig(store.active);
  if (config && isConfigValid(config)) return config;
  // Fallback: first valid provider
  for (const id of Object.keys(store.keys)) {
    const c = buildConfig(id);
    if (c && isConfigValid(c)) return c;
  }
  return null;
};

/**
 * Get all valid configs for fallback iteration
 * @returns {Array<{ provider, apiKey, endpoint, model }>}
 */
export const getValidConfigs = () => {
  const store = readStore();
  if (store.active === NO_AI) return []; // user opted out of AI — data engine only
  const configs = [];
  // Active provider first
  if (store.active && store.keys[store.active]) {
    const c = buildConfig(store.active);
    if (c && isConfigValid(c)) configs.push(c);
  }
  // Then remaining providers
  for (const id of Object.keys(store.keys)) {
    if (id === store.active) continue;
    const c = buildConfig(id);
    if (c && isConfigValid(c)) configs.push(c);
  }
  return configs;
};

/**
 * Check if at least one valid provider is configured
 */
export const hasValidConfig = () => {
  const store = readStore();
  return Object.keys(store.keys).some(id => {
    const c = buildConfig(id);
    return c && isConfigValid(c);
  });
};

/**
 * Should the AI path run? False when the user opted out (NO_AI) even if a key exists —
 * in that case the local Timechain Stats AI engine is used instead.
 */
export const shouldUseAI = () => {
  const store = readStore();
  if (store.active === NO_AI) return false;
  return hasValidConfig();
};

/**
 * Count of providers with a valid key/endpoint, regardless of the active/NO_AI choice.
 * Used for the "N providers" footer label.
 */
export const getConfiguredCount = () => {
  const store = readStore();
  return Object.keys(store.keys).filter(id => {
    const c = buildConfig(id);
    return c && isConfigValid(c);
  }).length;
};

// Rough token-count estimate for the footer (count only — no pricing).
const BASE_SYSTEM_TOKENS = 5100;
const ESTIMATED_OUTPUT_TOKENS = 800;
const CATEGORY_TOKEN_ESTIMATES = { market: 150, network: 200, mining: 250, lightning: 100, mempool: 200 };

export const getEstimatedTokens = (selectedCategories = null) => {
  const cats = (!Array.isArray(selectedCategories) || selectedCategories.length === 0)
    ? Object.keys(CATEGORY_TOKEN_ESTIMATES)
    : selectedCategories;
  const catTokens = cats.reduce((t, c) => t + (CATEGORY_TOKEN_ESTIMATES[c] || 0), 0);
  return BASE_SYSTEM_TOKENS + catTokens + ESTIMATED_OUTPUT_TOKENS;
};

/**
 * Check if a specific config object is valid
 */
export const isConfigValid = (config) => {
  if (!config) return false;
  const provider = AI_PROVIDERS[config.provider];
  if (!provider) return false;
  if (provider.requiresKey && !config.apiKey) return false;
  if (provider.requiresEndpoint && !config.endpoint) return false;
  return true;
};

/**
 * Clear all stored configs
 */
export const clearStoredConfig = () => {
  localStorage.removeItem(V2_STORAGE_KEY);
  localStorage.removeItem(V1_STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
};
